// Sincronizzazione dell'app dell'auditor: svuota la coda quando c'e' rete, in ordine, senza doppioni.
//   1) aperture dei sopralluoghi  2) foto e documenti (con identificativi del tablet)  3) tutte le altre operazioni
//   4) copia locale riallineata allo stato del server (se nel frattempo non e' cambiato nulla sul tablet).
// Un'operazione esce dalla coda SOLO con una risposta definitiva del server; un errore di rete la lascia dov'e'.
import { API_BASE } from "@/api/client";
import { blobDelete, blobGet, extFor, getSurvey, kvGet, kvPut, listSurveys, putSurvey, queueAll, queueDelete, queuePut } from "./fieldStore";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("dvr_token") || ""}` });
const MAX_RETRY = 8;

class NetError extends Error {}

async function call(path, options = {}) {
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...options, headers: { ...(options.body && !(options.body instanceof FormData) ? { "Content-Type": "application/json" } : {}), ...auth(), ...(options.headers || {}) } });
  } catch (e) {
    throw new NetError(e.message || "rete non raggiungibile");
  }
  if (res.status === 401) throw new NetError("sessione scaduta: rientra con la tua password");
  if (res.status >= 500) throw new NetError(`il server non risponde (${res.status})`);
  let data = null;
  try { data = await res.json(); } catch { /* corpo vuoto */ }
  return { status: res.status, ok: res.ok, data };
}

const detail = (data) => (data && (typeof data.detail === "string" ? data.detail : data.detail && data.detail.message)) || "non accettato";

async function addProblem(surveyId, message) {
  const rec = await getSurvey(surveyId);
  if (!rec) return;
  rec.problems = [...(rec.problems || []), { at: new Date().toISOString(), message }].slice(-20);
  await putSurvey(rec);
}

async function syncOps(ops) {
  const r = await call("/field/sync", { method: "POST", body: JSON.stringify({ ops: ops.map((o) => ({ uid: o.uid, type: o.type, survey_id: o.survey_id, data: o.data || {} })) }) });
  if (!r.ok) throw new NetError(`sincronizzazione non riuscita (${r.status})`);
  return r.data;
}

// ---- scarica il pacchetto per lavorare senza rete, solo se e' cambiato
export async function ensurePack() {
  const have = await kvGet("pack");
  try {
    const h = await call("/field/pack-hash");
    if (h.ok && have && have.hash === h.data.hash) return have;
    const p = await call("/field/pack");
    if (p.ok) { await kvPut("pack", p.data); return p.data; }
  } catch { /* offline: si usa quello che c'e' */ }
  return have;
}

export async function flush(onStep) {
  const res = { ok: true, sent: 0, problems: 0, error: null, touched: [] };
  if (typeof navigator !== "undefined" && navigator.onLine === false) return { ...res, ok: false, error: "offline" };
  const touched = new Set();
  try {
    let ops = await queueAll();
    const live = () => ops.filter((o) => !o.failed);

    // 1) aperture
    const starts = live().filter((o) => o.type === "start");
    if (starts.length) {
      const out = await syncOps(starts);
      for (const r of out.results) {
        const op = starts.find((o) => o.uid === r.uid);
        if (!op) continue;
        touched.add(op.survey_id);
        if (r.status === "rejected") {
          res.problems += 1;
          await addProblem(op.survey_id, `Apertura non accettata dal server: ${r.message}`);
          for (const o of ops.filter((x) => x.survey_id === op.survey_id)) { if (o.type === "blob") await blobDelete(o.uuid); await queueDelete(o.qid); }
        } else { await queueDelete(op.qid); res.sent += 1; }
      }
      ops = await queueAll();
      if (onStep) onStep();
    }

    // 2) foto e documenti
    for (const op of live().filter((o) => o.type === "blob")) {
      if (ops.some((o) => o.type === "start" && o.survey_id === op.survey_id)) continue; // l'apertura non e' ancora andata
      const blob = await blobGet(op.uuid);
      if (!blob) { await queueDelete(op.qid); continue; }
      const fd = new FormData();
      fd.append("file", blob, `${op.uuid}.${extFor(blob)}`);
      fd.append("kind", (op.meta && op.meta.kind) || "photo");
      fd.append("client_uuid", op.uuid);
      for (const k of ["question_code", "task_code", "equipment_code"]) if (op.meta && op.meta[k]) fd.append(k, op.meta[k]);
      const r = await call(`/field/surveys/${op.survey_id}/evidence`, { method: "POST", body: fd });
      touched.add(op.survey_id);
      if (r.ok) { await queueDelete(op.qid); await blobDelete(op.uuid); res.sent += 1; }
      else if ([404, 409].includes(r.status)) { await queueDelete(op.qid); await blobDelete(op.uuid); await addProblem(op.survey_id, "Una foto non e' stata salvata: il sopralluogo non si puo' piu' modificare."); res.problems += 1; }
      else if ([400, 413, 415, 422].includes(r.status)) { await queueDelete(op.qid); await blobDelete(op.uuid); await addProblem(op.survey_id, `Una foto non e' stata accettata: ${detail(r.data)}`); res.problems += 1; }
      else throw new NetError(`foto non inviata (${r.status})`);
      if (onStep) onStep();
    }

    // 3) tutto il resto, in ordine, a gruppi
    ops = await queueAll();
    const rest = ops.filter((o) => !o.failed && o.type !== "blob" && o.type !== "start");
    for (let i = 0; i < rest.length; i += 100) {
      const batch = rest.slice(i, i + 100);
      const out = await syncOps(batch);
      for (const r of out.results) {
        const op = batch.find((o) => o.uid === r.uid);
        if (!op) continue;
        touched.add(op.survey_id);
        if (r.status === "retry") {
          const tries = (op.tries || 0) + 1;
          if (tries >= MAX_RETRY) { await queueDelete(op.qid); await addProblem(op.survey_id, `Un dato non e' stato inviato: ${r.message}`); res.problems += 1; }
          else await queuePut({ ...op, tries });
        } else {
          await queueDelete(op.qid);
          if (r.status === "rejected") {
            res.problems += 1;
            await addProblem(op.survey_id, `${r.message}`);
            if (op.type === "send") { const rec = await getSurvey(op.survey_id); if (rec) { rec.send_queued = false; await putSurvey(rec); } }
          } else res.sent += 1;
        }
      }
      if (onStep) onStep();
    }
  } catch (e) {
    return { ...res, ok: false, error: e.message || "rete non raggiungibile", touched: [...touched] };
  }

  // 4) riallineamento con il server, solo per i sopralluoghi senza nulla in coda
  try {
    const left = await queueAll();
    const list = await call("/field/surveys");
    if (list.ok) {
      const local = Object.fromEntries((await listSurveys()).map((r) => [r.id, r]));
      for (const row of list.data.surveys) {
        const rec = local[row.id];
        const pending = left.some((o) => o.survey_id === row.id);
        if (pending) continue;
        if (rec && rec.server && rec.server_updated_at === row.updated_at && !touched.has(row.id)) continue;
        const rev = rec ? rec.rev || 0 : 0;
        const st = await call(`/field/surveys/${row.id}/state`);
        if (!st.ok) continue;
        const cur = await getSurvey(row.id);
        if ((cur ? cur.rev || 0 : 0) !== rev || (await queueAll()).some((o) => o.survey_id === row.id)) continue; // cambiato nel frattempo
        const d = st.data;
        await putSurvey({ ...(cur || {}), id: row.id, company_id: d.view.survey.company_id, title: d.view.survey.title,
          company: d.company, meta: d.meta, document_kinds: d.document_kinds, task_codes: d.task_codes, answers: d.answers,
          documents: d.documents, equipment_units: d.equipment_units, send_queued: false, problems: (cur && cur.problems) || [],
          server: { state: d.view.state, change_request: d.view.change_request, plans: d.view.plans, survey: { submitted: d.view.survey.submitted } },
          server_updated_at: row.updated_at, rev: (cur ? cur.rev || 0 : 0) });
      }
    }
  } catch { /* il riallineamento puo' aspettare il prossimo giro */ }
  res.touched = [...touched];
  return res;
}
